// apps/fe/src/modules/catalog/hooks/use-filters.ts

'use client';
import { useCallback, useEffect, useMemo } from 'react';
import { EventBus } from '@/shared/utils';
import { CatalogEvents, CatalogEventKeys } from '../events';
import { useBaseFilter } from './use-base-filter';
import { getFilteredData } from '../services/filtered-data-service';
import { CatalogFilters } from '../types/filter';
import { CourseDataFilters } from '../types/filtered-data';
import { hasActiveFilters } from '../utils/common';

interface UseFiltersProps {
  eventBus: EventBus<CatalogEvents>;
  filters: CatalogFilters;
  withAuthentication?: boolean;
  isAnyFilterActive?: boolean;
  onResetSidebarFilters?: () => void;
}

/**
 * Hook for general catalog filtering functionality.
 * Builds on the base filtering hook with filter-specific logic.
 */
export function useFilters({ eventBus, filters, withAuthentication = false }: UseFiltersProps) {
  // Use the base filter hook for common filtering functionality
  const baseHook = useBaseFilter({
    eventBus,
    filters,
    fetchDataFn: getFilteredData,
    eventKeys: CatalogEventKeys.filter,
    useAuthentication: withAuthentication, // Pass the authentication flag to useBaseFilter
  });

  // Determine if we're actively filtering - memoized to maintain reference equality
  const filteringCatalogData = useMemo(
    () => hasActiveFilters(baseHook.activeFilters),
    [baseHook.activeFilters],
  );

  // Set up event listener for filter.do events - with proper cleanup
  useEffect(() => {
    const handleFilterEvent = (data: { filters: Partial<CourseDataFilters> }) => {
      baseHook.handleFilter(data);
    };

    eventBus.on(CatalogEventKeys.filter.do, handleFilterEvent);

    return () => {
      eventBus.off(CatalogEventKeys.filter.do, handleFilterEvent);
    };
  }, [eventBus, baseHook]);

  // Filter-specific handlers - memoized to maintain reference equality
  const handleFilterAction = useCallback(
    (filters: Partial<CourseDataFilters>) => {
      eventBus.emit(CatalogEventKeys.filter.do, { filters });
    },
    [eventBus],
  );

  return {
    ...baseHook,
    filteringCatalogData,
    handleFilterAction,
  };
}
